/* global Office, window */
// The project setup window ("ახალი პროექტი" / "პროექტის პარამეტრები") runs in an Office dialog
// (displayDialogAsync) on the same taskpane.html, route #/project-setup. A dialog cannot read
// OfficeRuntime.storage, so the taskpane hands it the token over messageChild (DialogApi 1.2).
//
// dialog → taskpane: { type: "ready" } | { type: "done", projectId, created } | { type: "cancel" }
// taskpane → dialog: { type: "init", token }

export const SETUP_ROUTE = "/project-setup";
const DIALOG_SIZE_PCT = { width: 80, height: 86 };

export function isSetupRoute() {
  return (window.location.hash || "").replace(/^#/, "").startsWith(SETUP_ROUTE);
}

export function setupRouteParams() {
  const query = (window.location.hash || "").split("?")[1] || "";
  const params = new URLSearchParams(query);
  return {
    mode: params.get("mode") === "edit" ? "edit" : "new",
    projectId: params.get("projectId") ? Number(params.get("projectId")) : null,
    inDialog: params.get("dialog") === "1",
  };
}

export function canOpenDialog() {
  return (
    typeof Office !== "undefined" &&
    !!Office.context?.ui?.displayDialogAsync &&
    !!Office.context.requirements?.isSetSupported?.("DialogApi", "1.2")
  );
}

const setupUrl = ({ mode, projectId, dialog }) => {
  const params = new URLSearchParams({ mode });
  if (projectId) params.set("projectId", String(projectId));
  if (dialog) params.set("dialog", "1");
  const { origin, pathname, search } = window.location;
  return `${origin}${pathname}${search}#${SETUP_ROUTE}?${params.toString()}`;
};

/** Hash for showing the same window inside the taskpane (no dialog support, e.g. a browser). */
export const setupHash = ({ mode, projectId }) => setupUrl({ mode, projectId, dialog: false }).split("#")[1];

/**
 * Opens the setup window. Resolves false when dialogs are not available (the caller then
 * navigates to the in-taskpane route instead). onDone({ projectId, created }) after a save.
 */
export function openSetupDialog({ token, mode, projectId, onDone, onClosed }) {
  if (!canOpenDialog()) return Promise.resolve(false);
  return new Promise((resolve) => {
    Office.context.ui.displayDialogAsync(
      setupUrl({ mode, projectId, dialog: true }),
      { ...DIALOG_SIZE_PCT, displayInIframe: false },
      (result) => {
        if (result.status !== Office.AsyncResultStatus.Succeeded) {
          resolve(false);
          return;
        }
        const dialog = result.value;
        dialog.addEventHandler(Office.EventType.DialogMessageReceived, (arg) => {
          let msg;
          try {
            msg = JSON.parse(arg.message);
          } catch (e) {
            return;
          }
          if (msg.type === "ready") dialog.messageChild(JSON.stringify({ type: "init", token }));
          if (msg.type === "done" || msg.type === "cancel") {
            dialog.close();
            if (msg.type === "done") onDone?.({ projectId: msg.projectId, created: !!msg.created });
            else onClosed?.();
          }
        });
        dialog.addEventHandler(Office.EventType.DialogEventReceived, () => onClosed?.());
        resolve(true);
      }
    );
  });
}

/** Dialog side: announce readiness and wait for the token. */
export function receiveInit() {
  return new Promise((resolve, reject) => {
    if (typeof Office === "undefined" || !Office.context?.ui?.addHandlerAsync) {
      reject(new Error("Not running in an Office dialog"));
      return;
    }
    Office.context.ui.addHandlerAsync(
      Office.EventType.DialogParentMessageReceived,
      (arg) => {
        try {
          const msg = JSON.parse(arg.message);
          if (msg.type === "init") resolve(msg);
        } catch (e) {
          reject(e);
        }
      },
      (registered) => {
        if (registered.status !== Office.AsyncResultStatus.Succeeded) {
          reject(new Error("Dialog messaging is not available"));
          return;
        }
        Office.context.ui.messageParent(JSON.stringify({ type: "ready" }));
      }
    );
  });
}

export function tellParent(message) {
  if (typeof Office !== "undefined" && Office.context?.ui?.messageParent) {
    Office.context.ui.messageParent(JSON.stringify(message));
  }
}
